<template>
  <div class="listing-search-wrap">
    <template v-if="isMobile">
      <button ref="trigger" type="button" class="listing-search-trigger" @click="sheetOpen = true">
        <SvgIcon name="search" :size="20" />
        <span class="listing-search-trigger__txt">
          <span class="listing-search-trigger__main">{{ locationLabel }}</span>
        </span>
      </button>
      <ProjectSearchSheet
        v-if="sheetOpen"
        :api="api"
        :bootstrap="bootstrap"
        :criteria-store="criteriaStore"
        :default-radius="DEFAULT_RADIUS"
        @close="closeSheet"
        @reset="resetAll"
        @search="submit"
        @pick-place="onPickPlace"
        @pick-project="onPickProject"
      />
    </template>
    <div v-if="!isMobile && popoverOpen" class="listing-search__scrim" @mousedown="close" />
    <div v-if="!isMobile" ref="barEl" class="listing-search" :class="{ 'is-active': popoverOpen }">
      <div
        ref="locationEl"
        class="listing-search__segment listing-search__segment--location"
        :class="{ 'is-open': popoverOpen }"
        @mousedown="onLocationMousedown"
      >
        <span class="listing-search__segment-label">{{ t('listing-search.location.label') }}</span>
        <input
          class="listing-search__segment-input"
          maxlength="75"
          :placeholder="t('listing-search.location.placeholder')"
          :value="locationInputValue"
          @focus="open"
          @input="onLocationInput($event.target.value)"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="popoverOpen"
          :aria-controls="locationPopover && locationPopover.listboxId"
          :aria-activedescendant="locationPopover && locationPopover.activeId"
          @keydown.down.prevent="locationPopover && locationPopover.moveHighlight(1)"
          @keydown.up.prevent="locationPopover && locationPopover.moveHighlight(-1)"
          @keydown.enter.prevent="locationPopover && locationPopover.pickHighlighted()"
        />
        <button
          v-if="locationFilled"
          type="button"
          class="listing-search__segment-clear"
          :aria-label="t('listing-search.location.clear')"
          @mousedown.stop
          @click.stop="clearLocation"
        >
          <SvgIcon name="x" :size="12" />
        </button>
      </div>

      <div v-if="popoverOpen" class="listing-search__popover" :style="popoverStyle" @mousedown.stop>
        <div class="listing-search__popover-inner">
          <PopoverLocation
            ref="locationPopover"
            :api="api"
            :bootstrap="bootstrap"
            :criteria-store="criteriaStore"
            :default-radius="DEFAULT_RADIUS"
            projects-first
            @pick-place="onPickPlace"
            @pick-project="onPickProject"
            @next="close"
          />
        </div>
      </div>

      <button type="button" class="listing-search__submit" @click="submit">
        <SvgIcon name="search" :size="22" />
        <span class="listing-search__submit-text">{{ t('listing-search.submit') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import PopoverLocation from './PopoverLocation.vue';
import ProjectSearchSheet from './ProjectSearchSheet.vue';
import { useProjectCriteria } from '../lib/useProjectCriteria';
import { t } from '../lib/i18n';
import { DEFAULT_RADIUS } from '../lib/projectsContract';

const props = defineProps({
  api: { type: Object, required: true },
  bootstrap: { type: Object, required: true }
});

const emit = defineEmits(['search', 'pick-project']);

const criteriaStore = useProjectCriteria();
const { criteria, update, reset, persist } = criteriaStore;

const mobileMediaQuery = window.matchMedia('(max-width: 1024px)');
const isMobile = ref(mobileMediaQuery.matches);
const onMediaChange = (event) => {
  isMobile.value = event.matches;
};

const sheetOpen = ref(false);
const trigger = ref(null);

const popoverOpen = ref(false);
const barEl = ref(null);
const locationEl = ref(null);
const popoverStyle = ref(null);

const POPOVER_MIN_WIDTH = 340;
// The popover opens 1rem below the bar; the margin covers that gap plus
// breathing room above the viewport bottom.
const POPOVER_VIEWPORT_MARGIN = 24;
const POPOVER_MIN_HEIGHT = 160;

const selectedPlace = ref(null);
const locationPopover = ref(null);

const locationFilled = computed(() => Boolean(criteria.loc));

// A typed place without coordinates filters nothing (see projectsApiParams),
// so the summary only claims a location once a place with coordinates is picked.
const locationLabel = computed(() => {
  if (criteria.long === null || criteria.lat === null) return t('listing-search.location.anywhere');
  return criteria.radius === null ? criteria.loc : `${criteria.loc} · ${criteria.radius} km`;
});

// While the location popover is open the input holds the raw editable query;
// closed, it doubles as the summary and shows the radius alongside the place.
const locationInputValue = computed(() => {
  if (popoverOpen.value) return criteria.loc;
  if (criteria.long === null || criteria.lat === null || criteria.radius === null) {
    return criteria.loc;
  }
  return `${criteria.loc} · ${criteria.radius} km`;
});

// Every filter change (including clearing) persists immediately, not only on
// submit.
watch(criteria, () => persist(), { deep: true });

// The filter bar's "Filters wissen" resets the shared criteria from outside
// this component; drop the local place so a later submit doesn't replay it.
watch(
  () => criteria.long,
  (long) => {
    if (long === null) selectedPlace.value = null;
  }
);

const measurePopover = () => {
  if (!locationEl.value || !barEl.value) return;
  const barWidth = barEl.value.offsetWidth;
  const width = Math.min(barWidth, Math.max(POPOVER_MIN_WIDTH, locationEl.value.offsetWidth));
  const left = Math.min(locationEl.value.offsetLeft, Math.max(0, barWidth - width));
  const maxHeight = Math.max(
    POPOVER_MIN_HEIGHT,
    window.innerHeight - barEl.value.getBoundingClientRect().bottom - POPOVER_VIEWPORT_MARGIN
  );
  popoverStyle.value = { left: `${left}px`, width: `${width}px`, maxHeight: `${maxHeight}px` };
};

const open = async () => {
  popoverOpen.value = true;
  await nextTick();
  measurePopover();
  const input = locationEl.value
    ? locationEl.value.querySelector('.listing-search__segment-input')
    : null;
  if (input && document.activeElement !== input) input.focus();
};

const close = () => {
  popoverOpen.value = false;
  popoverStyle.value = null;
};

// Opening the segment should land focus in the input right away; preventDefault
// stops the browser from stealing focus back on mousedown (except on the input
// itself, where the default click places the cursor).
const onLocationMousedown = (event) => {
  if (!event.target.closest('.listing-search__segment-input')) event.preventDefault();
  open();
};

const onEscape = () => {
  close();
  const input = locationEl.value
    ? locationEl.value.querySelector('.listing-search__segment-input')
    : null;
  if (input) input.focus();
};

const onLocationInput = (value) => {
  selectedPlace.value = null;
  update({ loc: value, long: null, lat: null });
};

const clearLocation = () => {
  selectedPlace.value = null;
  update({ loc: '', long: null, lat: null, radius: DEFAULT_RADIUS });
};

const closeSheet = () => {
  sheetOpen.value = false;
  if (trigger.value) trigger.value.focus();
};

const resetAll = () => {
  selectedPlace.value = null;
  reset();
};

const onPickPlace = (place) => {
  selectedPlace.value = place;
};

const onPickProject = (project) => {
  close();
  emit('pick-project', project);
};

const submit = () => {
  close();
  sheetOpen.value = false;
  emit('search', { place: selectedPlace.value });
};

const onDocMousedown = (event) => {
  if (barEl.value && !barEl.value.contains(event.target)) close();
};

const onDocKeydown = (event) => {
  if (event.key === 'Escape' && popoverOpen.value) onEscape();
};

const onResize = () => {
  if (popoverOpen.value) measurePopover();
};

onMounted(() => {
  document.addEventListener('mousedown', onDocMousedown);
  document.addEventListener('keydown', onDocKeydown);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onResize, { passive: true });
  mobileMediaQuery.addEventListener('change', onMediaChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMousedown);
  document.removeEventListener('keydown', onDocKeydown);
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onResize);
  mobileMediaQuery.removeEventListener('change', onMediaChange);
});
</script>
